import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/Api';
import {
    CButton,
    CCard,
    CCardBody,
    CCol,
    CContainer,
    CForm,
    CFormInput,
    CInputGroup,
    CInputGroupText,
    CRow,
    CSpinner,
    CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilLockLocked, cilUser } from '@coreui/icons';
import sap_logo from 'assets/brand/sap_logo.png';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [invalidCredentials, setInvalidCredentials] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const redirectTarget = useMemo(() => {
        const redirectParam = new URLSearchParams(location.search).get('redirect');
        const fromState = location.state?.from
            ? `${location.state.from.pathname || ''}${location.state.from.search || ''}${location.state.from.hash || ''}`
            : '';
        const target = redirectParam || fromState || '/';

        if (!target.startsWith('/') || target.startsWith('//') || target.startsWith('/login')) {
            return '/';
        }

        return target;
    }, [location.search, location.state]);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (token) {
            navigate(redirectTarget, { replace: true });
        }
    }, [navigate, redirectTarget]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setInvalidCredentials(false);

        try {
            const response = await apiClient.post('/token/', { email, password });

            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.setItem('access_token', response.data.access);
            localStorage.setItem('refresh_token', response.data.refresh);

            navigate(redirectTarget, { replace: true });
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Credenciais Erradas');
                setInvalidCredentials(true);
            } else {
                setError('Erro no servidor. Tente novamente mais tarde.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-body-tertiary min-vh-100 d-flex flex-row align-items-center">
            <CContainer>
                <CRow className="justify-content-center g-0">
                    <CCol md={4}>
                        <CCard className="p-4 h-100 border-0 shadow-lg" color="dark" style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
                            <CCardBody className="d-flex justify-content-center align-items-center h-100">
                                <img
                                    src={sap_logo}
                                    alt="Logo"
                                    style={{
                                        height: 200,
                                        width: 200,
                                        objectFit: 'contain',
                                    }}
                                />
                            </CCardBody>
                        </CCard>
                    </CCol>
                    <CCol md={8}>
                        <CCard className="p-4 border-0 shadow-lg" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                            <CCardBody>
                                <h1 className="mb-4">Login</h1>
                                {error && (
                                    <CAlert color="danger" className="mb-4">
                                        {error}
                                    </CAlert>
                                )}
                                <CForm onSubmit={submit}>
                                    <CInputGroup className="mb-3">
                                        <CInputGroupText>
                                            <CIcon icon={cilUser} />
                                        </CInputGroupText>
                                        <CFormInput
                                            placeholder="Email"
                                            type="email"
                                            name="email"
                                            autoComplete="username"
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            value={email}
                                            required
                                            onChange={(e) => setEmail(e.target.value)}
                                            invalid={invalidCredentials}
                                        />
                                    </CInputGroup>
                                    <CInputGroup className="mb-4">
                                        <CInputGroupText>
                                            <CIcon icon={cilLockLocked} />
                                        </CInputGroupText>
                                        <CFormInput
                                            type="password"
                                            placeholder="Password"
                                            value={password}
                                            required
                                            onChange={(e) => setPassword(e.target.value)}
                                            invalid={invalidCredentials}
                                        />
                                    </CInputGroup>
                                    <CRow>
                                        <CCol xs={6}>
                                            <CButton color="primary" type="submit" className="px-4" disabled={loading}>
                                                Login
                                                {loading && (<CSpinner size="sm" className="ms-2" />)}
                                            </CButton>
                                        </CCol>
                                        <CCol xs={6} className="d-flex justify-content-end align-items-center">
                                            <CButton color="link" className="px-0" onClick={() => navigate('/password-reset')}>
                                                Recuperar password
                                            </CButton>
                                        </CCol>
                                    </CRow>
                                </CForm>
                            </CCardBody>
                        </CCard>
                    </CCol>
                </CRow>
            </CContainer>
        </div>
    );
};

export default Login;
